import { assert, objectEntries } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, NotificationSeverity, selectToasts, type ToastAction, type ToastItem } from '../../../domain'

import { Usecase } from '../Usecase'

type ToastOptions = Readonly<{
    severity: NotificationSeverity
    title: string
    description?: string
    action?: ToastAction
    /** Toasts sharing the same `dedupeKey` while visible aggregate into one, with a counter badge, instead of stacking. */
    dedupeKey?: string
    /**
     * When true, the toast never auto-dismisses on its own - the caller must call `dismiss()`
     * explicitly. For a toast reflecting an ongoing state the user hasn't resolved yet (e.g.
     * `PermissionsReducedModal`'s post-dismiss reminder), not a one-off event that should time out.
     */
    persist?: boolean
}>

const TOTAL_MAX = 3

const SEVERITY_MAX: Readonly<Record<NotificationSeverity, number>> = {
    [NotificationSeverity.INFO]: 1,
    [NotificationSeverity.SUCCESS]: 1,
    [NotificationSeverity.WARNING]: 2,
    [NotificationSeverity.ERROR]: 2,
}

/** Lower rank = higher priority = evicted last, higher rank = lower priority = evicted first. */
const EVICTION_RANK: Readonly<Record<NotificationSeverity, number>> = {
    [NotificationSeverity.ERROR]: 0,
    [NotificationSeverity.WARNING]: 1,
    [NotificationSeverity.INFO]: 2,
    [NotificationSeverity.SUCCESS]: 3,
}

export class ToastUsecase extends Usecase {
    private nextId = 0

    /**
     * Enforces the two caps from the Figma spec that notistack has no equivalent for - its own
     * `maxSnack` is a single severity-blind count that evicts whatever's oldest overall, with no
     * concept of "keep the error, drop the info." This evicts in two independent passes:
     *
     * 1. Per-severity: each severity has its own max (`SEVERITY_MAX` - info/success 1, warning/error
     *    2). A severity over its own max evicts its oldest entry, regardless of what else is queued.
     * 2. Total: the whole queue is capped at `TOTAL_MAX` (3). Over that, the *oldest entry of the
     *    lowest-priority severity currently present* is evicted (`EVICTION_RANK`) - e.g. an info
     *    toast is dropped before a warning or error would be, even if the info arrived more recently.
     *
     * Runs after `show()` has already resolved dedup/aggregation, so `queue` here is the candidate
     * post-aggregation state, not yet guaranteed to respect either cap.
     *
     * A `persist` toast is excluded from both passes entirely - it doesn't count toward either cap's
     * threshold, and is never picked as an eviction candidate. Otherwise unrelated toast traffic could
     * silently evict a toast whose whole contract is "only `dismiss()` removes me".
     */
    private static applyCaps (queue: ReadonlyArray<ToastItem>): ReadonlyArray<ToastItem> {
        let result = queue

        for (const [severity, max] of objectEntries(SEVERITY_MAX)) {
            while (result.filter((toast) => toast.severity === severity && !toast.persist).length > max) {
                result = ToastUsecase.evictOldestOfSeverity(result, severity)
            }
        }

        while (result.filter((toast) => !toast.persist).length > TOTAL_MAX) {
            const lowestPresentEntry = objectEntries(EVICTION_RANK)
                .filter(([severity]) => result.some((toast) => toast.severity === severity && !toast.persist))
                .sort(([, rankA], [, rankB]) => rankB - rankA)
                .at(0)

            assert(
                lowestPresentEntry,
                new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Toast queue overflowed with no severity to evict', { queue: result })
            )

            const [lowestPresentSeverity] = lowestPresentEntry

            result = ToastUsecase.evictOldestOfSeverity(result, lowestPresentSeverity)
        }

        return result
    }

    /**
     * Never targets a `persist` toast - see `applyCaps` above. Both of `applyCaps`'s call sites
     * already guarantee a matching entry exists before calling this, so a miss here means one of
     * those guards broke, not a normal outcome to fall back from.
     */
    private static evictOldestOfSeverity (queue: ReadonlyArray<ToastItem>, severity: NotificationSeverity): ReadonlyArray<ToastItem> {
        const index = queue.findIndex((toast) => toast.severity === severity && !toast.persist)

        assert(
            index !== -1,
            new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'No non-persistent toast of the given severity to evict', { queue, severity })
        )

        return [...queue.slice(0, index), ...queue.slice(index + 1)]
    }

    /**
     * Enqueues a toast, aggregating into an existing one sharing the same `dedupeKey` (while it's
     * still visible) instead of stacking a duplicate, then applies the per-severity and total caps.
     *
     * Unlike `description`/`action` (intentionally not sticky - each call's own options win outright),
     * `persist` falls back to the existing item's value when the new call omits it. Otherwise a caller
     * that re-fires `show()` for the same `dedupeKey` without repeating `persist: true` would silently
     * un-persist an already-shown toast.
     */
    show (options: ToastOptions): void {
        const id = options.dedupeKey ?? `toast-auto-${this.nextId++}`
        const queue = selectToasts(this.getState())
        const existing = queue.find((toast) => toast.id === id)
        const persist = options.persist ?? existing?.persist

        const item: ToastItem = {
            id,
            severity: options.severity,
            title: options.title,
            count: (existing?.count ?? 0) + 1,
            ...(options.description !== undefined && { description: options.description }),
            ...(options.action !== undefined && { action: options.action }),
            ...(persist !== undefined && { persist }),
        }

        const withoutExisting = queue.filter((toast) => toast.id !== id)

        this.setState({ toasts: ToastUsecase.applyCaps([...withoutExisting, item]) })
    }

    /** Removes a toast, e.g. once the caller-driven auto-dismiss/close affordance fires. */
    dismiss (id: ToastItem['id']): void {
        this.setState({ toasts: selectToasts(this.getState()).filter((toast) => toast.id !== id) })
    }
}
